(function (global) {
  'use strict';
  const model = typeof module === 'object' && module.exports
    ? require('./replay-workshop-model.js')
    : global.ReplayWorkshopModel;
  const playIcon = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7Z"/></svg>';
  const pauseIcon = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 5h4v14H7zm6 0h4v14h-4z"/></svg>';

  function init(root) {
    if (!root || !model || root.dataset.rwReady) return null;
    const document = root.ownerDocument;
    const window = document.defaultView;
    const find = (name) => root.querySelector(`[data-rw-${name}]`);
    const all = (name) => [...root.querySelectorAll(`[data-rw-${name}]`)];
    const player = find('player');
    const stage = find('stage');
    const seekInput = find('seek');
    const playButton = find('play');
    const cursor = find('cursor');
    if (!player || !stage || !seekInput || !playButton) return null;
    const sceneNodes = all('scene');
    const momentButtons = all('moment');
    const speedButtons = all('speed');
    const jumpButtons = all('jump');
    const workspace = player.closest('.rw-workspace') || player;
    const sceneMap = new Map(sceneNodes.map((scene) => [scene.dataset.rwScene, scene]));
    const anchorNodes = new Map(all('anchor').map((element) => [element.dataset.rwAnchor, element]));
    let anchorPoints = new Map();
    let time = 0;
    let speed = 1;
    let skipIdle = true;
    let playing = false;
    let started = false;
    let raf = null;
    let lastTick = null;
    let lastMoment = -1;
    let lastPage = '';
    let lastStatus = '';
    let lastPlaying = null;
    let skipTimer = null;
    let flashTimer = null;
    let expanded = false;
    let originOpen = false;
    let restoreFocus = null;
    let savedOverflow = '';
    let savedRole = null;
    let hadExpandedClass = false;
    let savedScroll = 0;
    let disposed = false;
    const cleanups = [];

    function listen(element, event, callback) {
      if (!element) return;
      element.addEventListener(event, callback);
      cleanups.push(() => element.removeEventListener(event, callback));
    }

    function setText(name, value) {
      const element = find(name);
      if (element && element.textContent !== String(value)) element.textContent = value;
    }

    function hide(name, hidden) {
      const element = find(name);
      if (element) element.hidden = hidden;
    }

    function clearSkipNotice() {
      if (skipTimer !== null) window.clearTimeout(skipTimer);
      skipTimer = null;
      hide('skip-notice', true);
    }

    function showSkipNotice(skipped) {
      clearSkipNotice();
      setText('skip-notice', `Skipped ${Math.round(skipped / 1000)} seconds with no interaction on the delivery cost`);
      hide('skip-notice', false);
      skipTimer = window.setTimeout(clearSkipNotice, 2600);
    }

    function measureAnchors(state) {
      anchorPoints = new Map();
      const stageBox = stage.getBoundingClientRect();
      anchorNodes.forEach((target, anchor) => {
        const scene = target.closest('[data-rw-scene]');
        if (scene && scene.dataset.rwScene !== state.page) return;
        const rect = target.getBoundingClientRect();
        anchorPoints.set(anchor, {
          left: rect.left - stageBox.left,
          top: rect.top - stageBox.top + (scene ? state.scroll : 0),
          width: rect.width, height: rect.height, scrolls: Boolean(scene)
        });
      });
    }

    function positionCursor(state, force) {
      if (!cursor) return;
      if (force || !anchorPoints.size) measureAnchors(state);
      const { from, to, progress } = state.pointer;
      const point = (frame) => {
        const rect = anchorPoints.get(frame.anchor);
        return rect && { x: rect.left + rect.width * frame.x, y: rect.top + rect.height * frame.y - (rect.scrolls ? state.scroll : 0) };
      };
      const start = point(from);
      const end = point(to) || start;
      if (start && end) {
        const distance = Math.hypot(end.x - start.x, end.y - start.y);
        const curve = Math.sin(progress * Math.PI) * Math.min(12, distance * .06);
        cursor.style.left = `${start.x + (end.x - start.x) * progress}px`;
        cursor.style.top = `${start.y + (end.y - start.y) * progress + curve}px`;
      }
      cursor.classList.toggle('is-clicked', state.clicked);
      cursor.hidden = originOpen;
    }

    function render() {
      const state = model.stateAt(time);
      root.toggleAttribute('data-rw-playing', playing);
      root.toggleAttribute('data-rw-started', started);
      player.setAttribute('aria-label', `Replay player, ${playing ? 'playing' : 'paused'} at ${model.formatTime(time)}. Space to play or pause; arrows to seek.`);
      if (lastPlaying !== playing) {
        playButton.innerHTML = playing ? pauseIcon : playIcon;
        lastPlaying = playing;
      }
      playButton.setAttribute('aria-label', playing ? 'Pause the replay' : state.ended ? 'Replay from the start' : 'Play the replay');
      playButton.title = playing ? 'Pause (Space)' : 'Play (Space)';
      hide('start', started || originOpen);
      hide('ended', !state.ended || originOpen);
      hide('added', !state.added);
      if (lastPage !== state.page) {
        sceneNodes.forEach((scene) => { scene.hidden = scene.dataset.rwScene !== state.page; });
        anchorPoints.clear();
        lastPage = state.page;
      }
      sceneMap.get(state.page)?.style.setProperty('--rw-scroll-y', `${-state.scroll}px`);
      setText('cart-count', state.cartCount);
      setText('host', originOpen ? 'Sample inbox' : 'solara.example');
      setText('path', originOpen ? '' : state.path);
      const pageLabel = { product: 'Product page', cart: 'Cart', shipping: 'Shipping information' }[state.page];
      setText('current-page', originOpen ? 'Email context' : state.ended ? 'Visit ended' : state.idle ? `${pageLabel} · idle` : pageLabel);
      const clock = model.formatTime(time);
      if (find('time').dataset.clock !== clock) {
        find('time').innerHTML = `${clock} <i>/ 0:58</i>`;
        find('time').dataset.clock = clock;
      }
      const eventLabel = originOpen ? 'Reconstructed sample email' : state.eventLabel;
      if (lastStatus !== eventLabel) { setText('event-label', eventLabel); lastStatus = eventLabel; }
      seekInput.value = String(time);
      seekInput.setAttribute('aria-valuetext', `${model.formatTime(time)} of 0:58, ${pageLabel}${state.idle ? ', idle' : ''}`);
      find('timeline').style.setProperty('--rw-progress', `${time / model.duration * 100}%`);
      speedButtons.forEach((button) => button.setAttribute('aria-pressed', String(Number(button.dataset.rwSpeed) === speed)));
      find('skip').setAttribute('aria-pressed', String(skipIdle));
      find('skip').title = skipIdle ? 'Skip the 20-second interval without activity' : 'Play every second, including the idle interval';
      if (state.activeIndex !== lastMoment) {
        momentButtons.forEach((button, index) => {
          button.classList.toggle('is-current', index === state.activeIndex);
          button.classList.toggle('is-past', index < state.activeIndex);
          if (index === state.activeIndex) button.setAttribute('aria-current', 'step');
          else button.removeAttribute('aria-current');
        });
        lastMoment = state.activeIndex;
      }
      setText('caption', originOpen ? 'This sample email leads to the Daily Cleanser product page.' : state.moment.description);
      positionCursor(state, false);
    }

    function pause() {
      playing = false;
      if (raf !== null) window.cancelAnimationFrame(raf);
      raf = null;
      lastTick = null;
      render();
    }

    function tick(timestamp) {
      raf = null;
      if (!playing || disposed) return;
      const elapsed = lastTick === null ? 0 : Math.max(0, timestamp - lastTick);
      lastTick = timestamp;
      const next = model.advance(time, elapsed, speed, skipIdle);
      time = next.time;
      if (next.skipped) showSkipNotice(next.skipped);
      if (next.ended) {
        playing = false;
        lastTick = null;
      }
      render();
      if (playing) raf = window.requestAnimationFrame(tick);
    }

    function closeOrigin() {
      originOpen = false;
      root.removeAttribute('data-rw-origin-open');
      hide('origin-scene', true);
      find('origin')?.setAttribute('aria-expanded', 'false');
    }

    function openOrigin() {
      pause();
      originOpen = true;
      root.setAttribute('data-rw-origin-open', '');
      hide('origin-scene', false);
      find('origin')?.setAttribute('aria-expanded', 'true');
      render();
    }

    function play() {
      if (disposed || playing) return;
      closeOrigin();
      if (time >= model.duration) time = 0;
      started = true;
      playing = true;
      lastTick = null;
      render();
      raf = window.requestAnimationFrame(tick);
    }

    function togglePlay() {
      if (playing) pause();
      else play();
      flash(playing ? 'Playing' : 'Paused');
    }

    function seek(value) {
      const resume = playing;
      pause();
      closeOrigin();
      started = true;
      time = model.clampTime(value);
      clearSkipNotice();
      render();
      if (resume && time < model.duration) play();
    }

    function setSpeed(value) {
      if (!model.speeds.includes(value)) return;
      speed = value;
      // The next animation frame starts a fresh interval at the new rate.
      lastTick = null;
      render();
    }

    function updateNoteButtons() {
      const valid = Boolean(model.createNote(find('next-check')?.value));
      if (find('copy')) find('copy').disabled = !valid;
      if (find('download')) find('download').disabled = !valid;
      if (!valid) setText('note-status', 'Add one next check to copy or download your note.');
      else setText('note-status', '');
      hide('copy-fallback', true);
    }

    function currentNote() {
      return model.createNote(find('next-check')?.value);
    }

    async function copyNote() {
      const note = currentNote();
      if (!note) return;
      try {
        if (!window.navigator.clipboard || typeof window.navigator.clipboard.writeText !== 'function') throw new Error('Clipboard unavailable');
        await window.navigator.clipboard.writeText(note);
        if (!disposed) setText('note-status', 'Template copied. Paste it wherever you keep your store notes.');
      } catch {
        if (disposed) return;
        find('fallback-text').value = note;
        hide('copy-fallback', false);
        find('fallback-text').focus();
        find('fallback-text').select();
        setText('note-status', 'Clipboard access is unavailable. Select and copy the template below.');
      }
    }

    function downloadNote() {
      const note = currentNote();
      if (!note) return;
      let url;
      let link;
      try {
        url = window.URL.createObjectURL(new window.Blob([note], { type: 'text/plain;charset=utf-8' }));
        link = document.createElement('a');
        link.href = url;
        link.download = 'myuser-ai-session-replay-review.txt';
        link.hidden = true;
        root.appendChild(link);
        link.click();
        setText('note-status', 'Download started: a review template, with this visit as the worked example.');
      } catch {
        setText('note-status', 'The download could not start. Use Copy template instead.');
      } finally {
        if (link) link.remove();
        if (url) window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      }
    }

    function flash(message) {
      if (!find('flash')) return;
      if (flashTimer !== null) window.clearTimeout(flashTimer);
      setText('flash', message);
      hide('flash', false);
      flashTimer = window.setTimeout(() => { hide('flash', true); flashTimer = null; }, 650);
    }

    function nudge(delta) {
      seek(time + delta);
      flash(`${delta < 0 ? '−' : '+'}${Math.abs(delta) / 1000}s`);
    }

    function toggleSkip() {
      skipIdle = !skipIdle;
      // The real player responds immediately when idle skipping is enabled.
      // Merely scrubbing into an idle band still preserves the chosen frame.
      if (skipIdle && model.stateAt(time).idle) {
        const next = model.advance(time, 0, speed, true);
        time = next.time;
        showSkipNotice(next.skipped);
      }
      render();
    }

    function setExpanded(value) {
      if (expanded === value) return;
      if (value) {
        // Read the reader's place BEFORE the player turns fixed: that change
        // alone re-anchors the page, and scroll anchoring would otherwise
        // throw them to the top. Anchoring stays off until they are back.
        savedScroll = window.scrollY || 0;
        document.documentElement.style.overflowAnchor = 'none';
      }
      expanded = value;
      root.toggleAttribute('data-rw-expanded', expanded);
      player.classList.toggle('is-expanded', expanded);
      const button = find('expand');
      button?.setAttribute('aria-pressed', String(expanded));
      button?.setAttribute('aria-label', expanded ? 'Exit expanded replay' : 'Expand the replay');
      if (button) button.title = expanded ? 'Exit expanded replay (Escape)' : 'Expand the replay (F)';
      if (expanded) {
        restoreFocus = document.activeElement;
        savedOverflow = document.body.style.overflow;
        savedRole = workspace.getAttribute('role');
        hadExpandedClass = document.body.classList.contains('rw-player-expanded');
        document.body.classList.add('rw-player-expanded');
        document.body.style.overflow = 'hidden';
        workspace.setAttribute('role', 'dialog');
        workspace.setAttribute('aria-modal', 'true');
        player.focus({ preventScroll: true });
      } else {
        document.body.style.overflow = savedOverflow;
        if (!hadExpandedClass) document.body.classList.remove('rw-player-expanded');
        if (savedRole) workspace.setAttribute('role', savedRole);
        else workspace.removeAttribute('role');
        workspace.removeAttribute('aria-modal');
        if (restoreFocus?.isConnected) restoreFocus.focus({ preventScroll: true });
        // Instant: the site scrolls smoothly, and gliding back would look like a jump.
        const back = () => window.scrollTo?.({ top: savedScroll, behavior: 'instant' });
        back();
        const settle = () => {
          back();
          document.documentElement.style.overflowAnchor = '';
        };
        if (window.requestAnimationFrame) window.requestAnimationFrame(settle);
        else settle();
      }
      positionCursor(model.stateAt(time), true);
    }

    function expandedKeys(event) {
      if (!expanded) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        setExpanded(false);
      } else if (event.key === 'Tab') {
        const controls = [...workspace.querySelectorAll('button:not(:disabled), input:not(:disabled), textarea:not(:disabled), a[href], summary, [tabindex="0"]')].filter((element) => {
          if (element.closest('[hidden]')) return false;
          const closedDetails = element.closest('details:not([open])');
          return !closedDetails || element === closedDetails.querySelector('summary');
        }).sort((a, b) => a.compareDocumentPosition(b) & 2 ? 1 : -1);
        const first = controls[0] || player;
        const last = controls[controls.length - 1] || player;
        if (!workspace.contains(document.activeElement)) {
          event.preventDefault(); (event.shiftKey ? last : first).focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first.focus();
        }
      }
    }

    function previewAt(value, percentage) {
      const state = model.stateAt(value);
      setText('scrub-preview', `${model.formatTime(state.time)} · ${state.idle ? 'Idle · ' : ''}${state.path}`);
      find('timeline').style.setProperty('--rw-hover', `${percentage}%`);
      hide('scrub-preview', false);
    }

    function onKeydown(event) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      const onSeek = event.target === seekInput;
      if (!onSeek && event.target.closest('input, textarea, select, a, summary, [contenteditable="true"]')) return;
      const key = event.key.toLowerCase();
      if (![' ', 'k', 'arrowleft', 'arrowright', 'j', 'l', 'home', 'end', '0', '[', ']', 's', 'f'].includes(key)) return;
      // The timeline is a slider: its arrows, Home and End keep their native meaning.
      if (onSeek && ['arrowleft', 'arrowright', 'home', 'end'].includes(key)) return;
      if (originOpen && key !== 'f') return;
      event.preventDefault();
      // Space continues to control playback after clicking a transport button;
      // focusing the player avoids the browser also activating that button.
      if (event.target.closest('button')) player.focus({ preventScroll: true });
      if (key === ' ' || key === 'k') togglePlay();
      else if (key === 'arrowleft' || key === 'j') nudge(-(key === 'j' ? 10000 : 5000));
      else if (key === 'arrowright' || key === 'l') nudge(key === 'l' ? 10000 : 5000);
      else if (key === 'home' || key === '0') seek(0);
      else if (key === 'end') seek(model.duration);
      else if (key === 's') toggleSkip();
      else if (key === 'f') setExpanded(!expanded);
      else setSpeed(model.speeds[Math.max(0, Math.min(model.speeds.length - 1, model.speeds.indexOf(speed) + (key === '[' ? -1 : 1)))]);
    }

    listen(find('start'), 'click', () => {
      player.scrollIntoView?.({ behavior: 'instant', block: 'start' });
      player.focus({ preventScroll: true });
      play();
    });
    listen(playButton, 'click', togglePlay);
    listen(find('restart'), 'click', () => { seek(0); play(); });
    const pagesMenu = root.querySelector('.rw-pages');
    listen(document, 'click', (event) => {
      if (pagesMenu?.open && !pagesMenu.contains(event.target)) pagesMenu.open = false;
    });
    listen(pagesMenu, 'keydown', (event) => {
      if (event.key !== 'Escape' || !pagesMenu.open) return;
      event.preventDefault();
      pagesMenu.open = false;
      pagesMenu.querySelector('summary')?.focus();
    });
    listen(stage, 'click', (event) => {
      if (pagesMenu?.open) return;
      if (originOpen || event.target.closest('button, a, input, textarea, summary')) return;
      player.focus({ preventScroll: true });
      togglePlay();
    });
    listen(find('back'), 'click', () => nudge(-10000));
    listen(find('forward'), 'click', () => nudge(10000));
    listen(seekInput, 'input', () => seek(seekInput.value));
    // A mouse or finger scrub hands the keyboard back to the player, so Space
    // and the arrows do what the shortcut list says straight afterwards.
    listen(seekInput, 'pointerup', () => player.focus({ preventScroll: true }));
    listen(find('skip'), 'click', toggleSkip);
    listen(find('expand'), 'click', () => setExpanded(!expanded));
    listen(find('origin'), 'click', () => {
      if (originOpen) { closeOrigin(); render(); }
      else openOrigin();
      revealPlayer();
    });
    listen(find('origin-follow'), 'click', () => { seek(0); play(); player.focus({ preventScroll: true }); });
    listen(workspace, 'keydown', onKeydown);
    listen(document, 'keydown', expandedKeys);
    speedButtons.forEach((button) => listen(button, 'click', () => setSpeed(Number(button.dataset.rwSpeed))));
    // On a phone the activity list sits below the player; a tap there must not
    // change a picture that is off screen.
    function revealPlayer() {
      if (expanded) return;
      const box = stage.getBoundingClientRect();
      const view = window.innerHeight || document.documentElement.clientHeight;
      if (box.height && (box.top < 0 || box.bottom > view)) {
        const calm = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        player.scrollIntoView?.({ behavior: calm ? 'auto' : 'smooth', block: 'center' });
      }
    }
    momentButtons.forEach((button) => listen(button, 'click', () => {
      const moment = model.moments.find((item) => item.id === button.dataset.rwMoment);
      if (moment) seek(moment.time);
      revealPlayer();
    }));
    // "Watch it happen" plays from just before the moment instead of landing
    // on a paused frame of it.
    all('watch').forEach((button) => listen(button, 'click', () => {
      seek(Math.max(0, Number(button.dataset.rwWatch) - 2000));
      player.focus({ preventScroll: true });
      if (!expanded) workspace.scrollIntoView?.({ behavior: 'instant', block: 'start' });
      play();
    }));
    jumpButtons.forEach((button) => listen(button, 'click', () => {
      seek(Number(button.dataset.rwJump));
      const details = button.closest('details');
      if (details) details.open = false;
      player.focus({ preventScroll: true });
      if (!expanded && !workspace.contains(button)) workspace.scrollIntoView?.({ behavior: 'instant', block: 'start' });
    }));
    listen(find('timeline'), 'pointermove', (event) => {
      const rect = find('timeline').getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / (rect.width || 1)));
      previewAt(model.duration * ratio, ratio * 100);
    });
    listen(find('timeline'), 'pointerleave', () => hide('scrub-preview', true));
    listen(seekInput, 'focus', () => previewAt(time, time / model.duration * 100));
    listen(seekInput, 'input', () => previewAt(time, time / model.duration * 100));
    listen(seekInput, 'blur', () => hide('scrub-preview', true));
    listen(find('clue'), 'click', () => {
      seek(18000);
      player.focus({ preventScroll: true });
      player.scrollIntoView({ behavior: 'auto', block: 'center' });
    });
    listen(find('next-check'), 'input', updateNoteButtons);
    listen(find('copy'), 'click', copyNote);
    listen(find('download'), 'click', downloadNote);
    listen(document, 'visibilitychange', () => { if (document.hidden) pause(); });
    listen(window, 'resize', () => positionCursor(model.stateAt(time), true));
    listen(window, 'pagehide', pause);

    if (cursor) cursor.style.transition = 'none';
    all('speed').concat(momentButtons, jumpButtons, all('watch'), [find('start'), playButton, find('back'), find('forward'), seekInput, find('skip'), find('clue'), find('expand'), find('origin'), find('origin-follow')]).forEach((element) => { if (element) element.disabled = false; });
    hide('note-section', false);
    root.dataset.rwReady = 'true';
    closeOrigin();
    updateNoteButtons();
    render();
    return {
      seek,
      play,
      pause,
      state: () => ({ ...model.stateAt(time), playing, speed, skipIdle, expanded, originOpen }),
      destroy() {
        pause();
        setExpanded(false);
        disposed = true;
        clearSkipNotice();
        if (flashTimer !== null) window.clearTimeout(flashTimer);
        cleanups.forEach((cleanup) => cleanup());
      }
    };
  }

  if (typeof module === 'object' && module.exports) module.exports = { init };
  else if (global.document) global.document.querySelectorAll('[data-replay-workshop]').forEach(init);
})(typeof globalThis !== 'undefined' ? globalThis : this);
